import { api } from "@/lib/api";

/**
 * One item on the bell.
 *
 * ⚠️ Carries the SUBJECT LINE and no body. The server never sends one: the stored body is
 * HTML built for email clients, and putting that in front of the browser would need
 * `dangerouslySetInnerHTML`, which is banned. In-app rows are written without a body at all,
 * so there is nothing here to render unsafely even by accident.
 *
 * ⚠️ Two numbers doing different jobs (D-4, D-35). `displayNumber` is what the item reads
 * as — `#412`, the reference people quote — and `number` is the opaque value the link is
 * built on. Never route on the readable one.
 *
 * ⚠️ Both are NULL on a follow-up reminder (D-95), which is about a private row on the reader's
 * own list rather than about a request and may name no request at all. The absent `number` is
 * what tells the bell to open `/todos` — the server withholds it exactly when there is nothing
 * to route to, so the rendering cannot disagree with the payload.
 */
export interface AppNotification {
  id: string;
  number: string | null;
  displayNumber: number | null;
  /** What HAPPENED — the rendered subject line of the message itself. */
  subject: string;

  /**
   * What it happened TO — the request's own title.
   *
   * ⚠️ Resolved live by the server through the query filter, never stored on the row: it is
   * free text somebody typed about their problem, so it is PHI-capable, and a copy frozen at
   * compose time would go on labelling a notification after the request was routed away, was
   * reclassified, or the reader was removed from it.
   *
   * ⚠️ Null for a follow-up reminder, which names no request (D-95), and for a request that
   * has gone out of reach. Both cases fall back to `subject` — deliberately the same answer,
   * because "you can no longer see this" and "there is nothing to see" are not a distinction
   * a bell should be drawing.
   */
  ticketSubject: string | null;

  /**
   * Whether `subject` says anything this item has not already said.
   *
   * ⚠️ True for the creation receipt and nothing else. In a mailbox "We have your request" is
   * the whole message; here it sits under the request's own title and above its requester and
   * age, where the notification EXISTING is the receipt — so a bell full of new requests was a
   * column of one repeated sentence.
   *
   * ⚠️ THE SERVER DECIDES, from the enum. Nothing in this file may branch on `eventType`
   * instead: a list of event names in TypeScript is a second list to keep in step with the
   * server's, and the day they disagree is the day a real notification renders wrong. Same
   * reason the bell decides "is this a follow-up" from the absent number rather than the
   * event name.
   */
  subjectRestatesTheRequest: boolean;

  /** Who raised it. A display name and nothing else — nothing on the bell links to a person. */
  requesterName: string | null;

  eventType: string;
  createdAtUtc: string;
  readAtUtc: string | null;

  /**
   * Whether the desktop hook may draw this item as an operating-system alert (D-155).
   *
   * ⚠️ Decided by the server from the person's own per-kind switches and the kind's default, and
   * always true for a mandatory item. A rendering instruction only: every item here is already
   * on the bell, and this says which of them the person asked to be interrupted for.
   */
  desktop: boolean;
}

export interface NotificationList {
  items: AppNotification[];

  /** Unread across everything, not just the items above. */
  unreadCount: number;
}

/**
 * The message's subject with its leading `[#412]` removed, for a surface that already says
 * which request this is.
 *
 * ⚠️ THE PREFIX IS FOR EMAIL AND ONLY FOR EMAIL. Every shipped template opens
 * `[#{{ticket.displayNumber}}]` because a mail subject arrives with no context around it —
 * it is a line in an inbox, and the number is the only thing that says what it concerns.
 * The bell has context: it prints `#412` on its own meta line beside the requester and the
 * age. So the item read `#412` twice, three lines apart, which is noise on the one control
 * that has to be scannable at a glance.
 *
 * ⚠️ REMOVED IN THE BROWSER, NOT FROM THE TEMPLATE, and the distinction matters. The
 * template is what an administrator edits and what a mailbox renders; editing it to suit
 * the bell would take the number off the emails, where it is doing the job it was written
 * for. This is one surface choosing not to repeat itself.
 *
 * ⚠️ IT ONLY EVER STRIPS THIS ITEM'S OWN NUMBER. An administrator can rewrite any template
 * (D-48), so the prefix is a convention rather than a guarantee, and a blind
 * `^\[#\d+\]\s*` would silently eat the opening of a subject somebody deliberately wrote
 * that way. Matching against `displayNumber` means the worst case is a prefix left in
 * place — visible, and the state we are coming from — rather than text quietly removed.
 *
 * Returns the subject untouched when there is no number (a follow-up reminder, D-95), when
 * the prefix names a different request, or when nothing is left after it: a stripped-to-empty
 * subject is a blank line where a sentence should be.
 */
export function withoutTicketNumberPrefix(subject: string, displayNumber: number | null): string {
  if (displayNumber === null) return subject;

  const prefix = `[#${displayNumber}]`;
  if (!subject.startsWith(prefix)) return subject;

  const rest = subject.slice(prefix.length).trim();

  return rest.length > 0 ? rest : subject;
}

export function getNotifications(): Promise<NotificationList> {
  return api<NotificationList>("/api/notifications");
}

export function markNotificationRead(id: string): Promise<void> {
  return api<void>(`/api/notifications/${id}/read`, { method: "POST" });
}

export function markAllNotificationsRead(): Promise<{ marked: number }> {
  return notificationsRead("/api/notifications/read");
}

/**
 * Clears the unread updates for one request, because it has just been read.
 *
 * ⚠️ WHAT MAKES THE HOME BADGE HONEST. Without this somebody reads every reply on a request,
 * goes back to the home screen and is still told there are three updates waiting — a badge
 * that outlives the act of reading, which teaches people to ignore it.
 *
 * ⚠️ The OPAQUE number (D-4). Never the readable one.
 *
 * ⚠️ Best effort by design. This is housekeeping on a screen that has already loaded, so a
 * failure must not surface as an error on a page somebody is reading. The worst case is the
 * badge clearing on the next visit instead of this one.
 */
export function markTicketNotificationsRead(number: string): Promise<{ marked: number }> {
  return notificationsRead(`/api/notifications/read?ticket=${encodeURIComponent(number)}`);
}

/**
 * Clears the unread follow-up reminders, because the list has just been opened (D-95).
 *
 * ⚠️ THE SAME RULE AS A REQUEST, applied to the only notification that has no request to be
 * cleared by. A reminder names no ticket, so `markTicketNotificationsRead` can never reach one —
 * without this the badge would survive the reader going to the screen, reading the note and
 * ticking it off, which is the badge-that-outlives-reading defect D-70 was written for.
 *
 * ⚠️ Best effort, like its sibling: housekeeping on a screen that has already loaded must not
 * surface as an error on a page somebody is using.
 */
export function markFollowUpNotificationsRead(): Promise<{ marked: number }> {
  return notificationsRead("/api/notifications/read?followUps=true");
}

/**
 * Clears the "somebody shared a request with you" marks, because the tab that lists them has
 * just been opened.
 *
 * ⚠️ THE SAME RULE A THIRD TIME (D-70). The mark on the "Shared with me" tab is counted from
 * these very rows, so a reader who opens the tab, sees the requests and comes back tomorrow
 * would otherwise still be told there is something new — and once a mark survives the act of
 * reading, people stop reading it.
 *
 * ⚠️ It clears them on the BELL as well, and that is correct rather than a side effect: the
 * two are one set of rows on purpose, so that they cannot disagree about what is waiting.
 * Opening a request already does exactly this to that request's own updates.
 *
 * ⚠️ Best effort, like both its siblings: housekeeping on a screen that has already loaded
 * must never surface as an error on the page somebody is using.
 */
export function markSharedWithMeNotificationsRead(): Promise<{ marked: number }> {
  return notificationsRead("/api/notifications/read?sharedWithMe=true");
}

/**
 * Posts, then tells the bell.
 *
 * The bell reloads on focus and visibility change and deliberately does not poll, so without
 * a nudge it would keep showing a count that a different part of the same page has already
 * cleared. A DOM event rather than shared state: the bell lives in the app chrome and the
 * callers are routes, and threading a context through every one of them to deliver "something
 * changed" is more machinery than the message deserves.
 */
async function notificationsRead(path: string): Promise<{ marked: number }> {
  const result = await api<{ marked: number }>(path, { method: "POST" });

  if (result.marked > 0) {
    window.dispatchEvent(new CustomEvent(NOTIFICATIONS_CHANGED));
  }

  return result;
}

export const NOTIFICATIONS_CHANGED = "notifications:changed";
